import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import axios from "axios";
import ReusableTable from "../../components/UI/ReusableTable";
import Notification from "../../components/UI/Notification";
import ReactPaginate from "react-paginate";
import { AuthContext } from "../../contexts/AuthContext";
import Modal from "../../components/UI/Modal";
import DeleteButton from "../../components/UI/AlertDialog";
import Drawer from "../../components/UI/Drawer";
import {
  User,
  Briefcase,
  MapPin,
  DollarSign,
  Mail,
  Phone,
  Clock,
  GraduationCap,
  Star,
  FileText,
  Download,
  CheckCircle,
  Building,
  Target,
  MoreVertical,
  AlertTriangle,
} from "lucide-react";
import { createPortal } from "react-dom";
import DropdownMenu from "../../components/UI/DropdownMenu";

// Custom Alert Dialog Component
const AlertDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-black p-6 rounded-t-lg md:rounded-lg shadow-lg w-full max-w-sm mx-auto transition-transform duration-300">
        <h2 className="text-lg font-semibold text-center dark:text-white">
          Are you sure?
        </h2>
        <p className="text-gray-600 mt-2 text-center dark:text-gray-500">
          This action cannot be undone.
        </p>
        <div className="mt-4 flex justify-between space-x-3">
          <button
            className="block w-full py-2 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-10 py-2 text-white bg-red-600 rounded hover:bg-red-700 w-full"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Application Details Component
const ApplicationDetails = ({ application, close }) => {
  if (!application) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">No application selected</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="mx-auto bg-white shadow-2xl overflow-hidden">
      {/* Header Section */}
      <div className="bg-blue-500 px-8 py-8 text-white font-poppins">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-semibold mb-2">
              {application.full_name}
            </h1>
            <div className="flex items-center space-x-6 text-blue-100">
              <div className="flex items-center space-x-2">
                <Briefcase className="h-5 w-5" />
                <span>{application.position_applied_for}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>{application.current_location}</span>
              </div>
            </div>
          </div>
          <button
            onClick={close}
            className="text-white dark:hover:text-gray-400 hover:bg-blue-800 hover:text-gray-200 text-xl px-2 py-1 rounded-md transition delay-100"
          >
            ✖
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8 font-poppins">
        {/* Key Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-blue-500 p-2 rounded-lg">
                <User className="h-5 w-5 text-white" />
              </div>
              <span className="text-blue-600 font-medium">Experience</span>
            </div>
            <p className="text-2xl font-semibold text-gray-900">
              {application.total_experience_years} years
            </p>
          </div>

          <div className="bg-green-50 rounded-xl p-6 border border-green-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-green-500 p-2 rounded-lg">
                <Building className="h-5 w-5 text-white" />
              </div>
              <span className="text-green-600 font-medium">
                Current Company
              </span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {application.current_company_name}
            </p>
          </div>

          <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-purple-500 p-2 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <span className="text-purple-600 font-medium">Expected CTC</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              ₹{application.expected_ctc_month}/month
            </p>
          </div>

          <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
            <div className="flex items-center space-x-3 mb-2">
              <div className="bg-orange-500 p-2 rounded-lg">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <span className="text-orange-600 font-medium">Contact</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {application.email}
            </p>
            <p className="text-sm text-gray-600">{application.phone_number}</p>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <User className="h-6 w-6 text-blue-600" />
            <span>Personal Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <User className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Full Name</span>
              </div>
              <p className="text-gray-700">{application.full_name}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Mail className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Email</span>
              </div>
              <p className="text-gray-700">{application.email}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Phone className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Phone</span>
              </div>
              <p className="text-gray-700">{application.phone_number}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <MapPin className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Location</span>
              </div>
              <p className="text-gray-700">{application.current_location}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <GraduationCap className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Education</span>
              </div>
              <p className="text-gray-700">{application.highest_education}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-gray-900">Notice Period</span>
              </div>
              <p className="text-gray-700">{application.notice_period}</p>
            </div>
          </div>
        </div>

        {/* Professional Details */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Briefcase className="h-6 w-6 text-green-600" />
            <span>Professional Details</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Building className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">
                  Current Company
                </span>
              </div>
              <p className="text-gray-700">
                {application.current_company_name}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <User className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">
                  Current Designation
                </span>
              </div>
              <p className="text-gray-700">{application.current_designation}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Target className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">
                  Position Applied
                </span>
              </div>
              <p className="text-gray-700">
                {application.position_applied_for}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <DollarSign className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">Current CTC</span>
              </div>
              <p className="text-gray-700">
                ₹{application.current_ctc_month}/month
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <DollarSign className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">Expected CTC</span>
              </div>
              <p className="text-gray-700">
                ₹{application.expected_ctc_month}/month
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Star className="h-5 w-5 text-green-600" />
                <span className="font-medium text-gray-900">Source</span>
              </div>
              <p className="text-gray-700">
                {application.source_of_application}
              </p>
            </div>
          </div>
        </div>

        {/* Job Role & Goals */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Target className="h-6 w-6 text-purple-600" />
            <span>Job Role & Goals</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Briefcase className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-gray-900">
                  Current Job Role
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">
                {application.current_job_role}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <FileText className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-gray-900">
                  Reason for Exploring
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">
                {application.reason_for_exploring}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Target className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-gray-900">
                  Short Term Goal
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">
                {application.short_term_goal}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Target className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-gray-900">
                  Long Term Goal
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">
                {application.long_term_goal}
              </p>
            </div>
          </div>
        </div>

        {/* SWOT Analysis */}
        {application.swot && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <FileText className="h-6 w-6 text-orange-600" />
              <span>SWOT Analysis</span>
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap">
                {application.swot}
              </p>
            </div>
          </div>
        )}

        {/* Ideal Candidate */}
        {application.ideal_candidate && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <User className="h-6 w-6 text-indigo-600" />
              <span>Ideal Candidate Description</span>
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap">
                {application.ideal_candidate}
              </p>
            </div>
          </div>
        )}

        {/* Preferences */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <CheckCircle className="h-6 w-6 text-yellow-600" />
            <span>Preferences</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <MapPin className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-gray-900">
                  Comfortable with Travel
                </span>
              </div>
              <p className="text-gray-700">
                {application.comfortable_travel === 1 ||
                application.comfortable_travel === true
                  ? "Yes"
                  : "No"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-gray-900">
                  Comfortable with Six Days
                </span>
              </div>
              <p className="text-gray-700">
                {application.comfortable_six_days === 1 ||
                application.comfortable_six_days === true
                  ? "Yes"
                  : "No"}
              </p>
            </div>
          </div>
        </div>

        {/* Resume */}
        {(application.resume || application.resume_url) && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Download className="h-6 w-6 text-red-600" />
              <span>Resume</span>
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <a
                href={application.resume_url || application.resume}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                View Resume
              </a>
            </div>
          </div>
        )}

        {/* Timeline Footer */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center space-x-4">
              <span>Submitted: {formatDate(application.created_at)}</span>
              {application.updated_at &&
                application.updated_at !== application.created_at && (
                  <>
                    <span>•</span>
                    <span>Updated: {formatDate(application.updated_at)}</span>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ApplicationResponses = () => {
  const { user, roles } = useContext(AuthContext) || {};
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const buttonRefs = useRef({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const handleClickOutside = (event) => {
    if (!event.target.closest(".dropdown-container")) {
      setOpenDropdownId(null);
    }
  };

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    setOpenDropdownId((prev) => (prev === requestId ? null : requestId));
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const fetchApplications = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `https://hrmsnew.prepstripe.com/api/applications?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setApplications(response.data.data || response.data.applications || []);
      setPagination({
        page: response.data.page || page,
        totalPages: response.data.total_pages || response.data.totalPages || 1,
      });
    } catch (error) {
      setNotification({
        message:
          error?.response?.data?.message || "Failed to fetch applications.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationDetails = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `https://hrmsnew.prepstripe.com/api/applications/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const applicationData = response.data.data || response.data;
      // console.log('Application Details:', applicationData);
      // console.log('Full Name:', applicationData.full_name);
      setSelectedApp(applicationData);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error("Error fetching application details:", error);
      setNotification({
        message: error?.response?.data?.message || "Failed to fetch details.",
        type: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `https://hrmsnew.prepstripe.com/api/applications/${deleteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setNotification({
        message: "Application deleted successfully.",
        type: "success",
      });
      fetchApplications(currentPage);
    } catch (error) {
      setNotification({
        message:
          error?.response?.data?.message || "Failed to delete application.",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteId(null);
  };

  const handleEdit = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `https://hrmsnew.prepstripe.com/api/applications/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setEditForm(response.data.data || response.data);
      setIsEditModalOpen(true);
    } catch (error) {
      setNotification({
        message:
          error?.response?.data?.message ||
          "Failed to fetch application for update.",
        type: "error",
      });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");
      let dataToSend = editForm;
      let config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      // If a new resume file is selected, use FormData
      if (editForm.resume && editForm.resume instanceof File) {
        const formData = new FormData();
        Object.entries(editForm).forEach(([key, value]) => {
          if (key === "resume" && value instanceof File) {
            formData.append("resume", value);
          } else if (
            key === "comfortable_travel" ||
            key === "comfortable_six_days"
          ) {
            formData.append(key, value ? "1" : "0");
          } else {
            formData.append(key, value);
          }
        });
        dataToSend = formData;
        config.headers["Content-Type"] = "multipart/form-data";
      } else {
        // For regular JSON updates, also convert booleans to '0'/'1'
        const jsonData = { ...editForm };
        if (jsonData.comfortable_travel !== undefined) {
          jsonData.comfortable_travel = jsonData.comfortable_travel ? "1" : "0";
        }
        if (jsonData.comfortable_six_days !== undefined) {
          jsonData.comfortable_six_days = jsonData.comfortable_six_days
            ? "1"
            : "0";
        }
        dataToSend = jsonData;
      }
      await axios.post(
        `https://hrmsnew.prepstripe.com/api/applications/${editForm.id}`,
        dataToSend,
        config,
      );
      setNotification({
        message: "Application updated successfully.",
        type: "success",
      });
      setIsEditModalOpen(false);
      fetchApplications(currentPage);
    } catch (error) {
      setNotification({
        message:
          error?.response?.data?.message || "Failed to update application.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchApplications(currentPage);
    // eslint-disable-next-line
  }, [currentPage]);

  const columns = useMemo(
    () => [
      {
        label: "Full Name",
        field: "full_name",
        type: "text",
        render: (row) => (
          <button
            onClick={() => fetchApplicationDetails(row.id)}
            className="text-blue-600 hover:underline hover:underline-offset-4 font-medium"
          >
            {row.full_name || "-"}
          </button>
        ),
      },
      { label: "Email", field: "email", type: "text" },
      { label: "Phone", field: "phone_number", type: "text" },
      {
        label: "Submitted At",
        field: "created_at",
        type: "text",
        render: (row) => {
          if (!row.created_at) return "-";
          const date = new Date(row.created_at);
          return date
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            .replace(/\//g, "-");
        },
      },
      {
        label: "Actions",
        field: "actions",
        type: "text",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                ref={(el) => {
                  if (el) buttonRefs.current[row.id] = el;
                }}
                onClick={(e) => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              {openDropdownId === row.id && (
                <DropdownMenu
                  buttonRef={buttonRefs.current[row.id]}
                  onClose={() => setOpenDropdownId(null)}
                >
                  <button
                    onClick={() => {
                      fetchApplicationDetails(row.id);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => {
                      handleEdit(row.id);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    Update
                  </button>
                  <DeleteButton
                    style={`w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20`}
                    onDelete={() => {
                      handleDelete(row.id);
                      setOpenDropdownId(null);
                    }}
                  />
                </DropdownMenu>
              )}
            </div>
          </div>
        ),
      },
    ],
    [isDeleting, openDropdownId, dropdownCoords, roles],
  );

  const handlePageChange = (event) => {
    setCurrentPage(event.selected + 1);
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-950 font-poppins rounded-lg">
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">
        Application Responses
      </h2>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <ReusableTable data={applications} loading={loading} columns={columns} />

      {applications.length > 0 && (
        <div className="mt-4">
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="pointer-events-none"
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          />
        </div>
      )}

      {/* Details Drawer */}
      <Drawer
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        border_radius="md:rounded-none"
        showClose={false}
        ChilDiv=""
        ChildParent=""
      >
        <ApplicationDetails
          application={selectedApp}
          close={() => setIsDetailsModalOpen(false)}
        />
      </Drawer>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        title="Update Application"
      >
        {editForm ? (
          <form onSubmit={handleEditSubmit} className="space-y-3">
            {/* Only show fields present in ApplicationFormPage.js, in the same order and type */}
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Full Name:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.full_name ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, full_name: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Phone Number:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.phone_number ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, phone_number: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">Email:</label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.email ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, email: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Total Experience (Years):
              </label>
              <input
                type="number"
                className="border px-2 py-1 rounded"
                value={editForm.total_experience_years ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    total_experience_years: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Current Location:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.current_location ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    current_location: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Highest Education:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.highest_education ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    highest_education: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Current Company Name:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.current_company_name ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    current_company_name: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Current Designation:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.current_designation ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    current_designation: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Current CTC (Month):
              </label>
              <input
                type="number"
                className="border px-2 py-1 rounded"
                value={editForm.current_ctc_month ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    current_ctc_month: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Expected CTC (Month):
              </label>
              <input
                type="number"
                className="border px-2 py-1 rounded"
                value={editForm.expected_ctc_month ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    expected_ctc_month: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Notice Period:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.notice_period ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, notice_period: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Position Applied For:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.position_applied_for ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    position_applied_for: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Source of Application:
              </label>
              <input
                className="border px-2 py-1 rounded"
                value={editForm.source_of_application ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    source_of_application: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Reason for Exploring:
              </label>
              <textarea
                className="border px-2 py-1 rounded"
                value={editForm.reason_for_exploring ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    reason_for_exploring: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Current Job Role:
              </label>
              <textarea
                className="border px-2 py-1 rounded"
                value={editForm.current_job_role ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    current_job_role: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Comfortable with travel requirements:
              </label>
              <select
                className="border px-2 py-1 rounded"
                value={editForm.comfortable_travel ? "1" : "0"}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    comfortable_travel: e.target.value === "1",
                  }))
                }
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Comfortable with six-day work week:
              </label>
              <select
                className="border px-2 py-1 rounded"
                value={editForm.comfortable_six_days ? "1" : "0"}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    comfortable_six_days: e.target.value === "1",
                  }))
                }
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Short Term Goal:
              </label>
              <textarea
                className="border px-2 py-1 rounded"
                value={editForm.short_term_goal ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    short_term_goal: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Long Term Goal:
              </label>
              <textarea
                className="border px-2 py-1 rounded"
                value={editForm.long_term_goal ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, long_term_goal: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">SWOT:</label>
              <textarea
                className="border px-2 py-1 rounded"
                value={editForm.swot ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, swot: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">
                Ideal Candidate:
              </label>
              <textarea
                className="border px-2 py-1 rounded"
                value={editForm.ideal_candidate ?? ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    ideal_candidate: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col">
              <label className="font-semibold capitalize mb-1">Resume:</label>
              {(typeof editForm.resume === "string" &&
                editForm.resume &&
                editForm.resume !== "null") ||
              (typeof editForm.resume_url === "string" &&
                editForm.resume_url &&
                editForm.resume_url !== "null") ? (
                <a
                  href={editForm.resume_url || editForm.resume}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline mb-2"
                >
                  View Current Resume
                </a>
              ) : null}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, resume: e.target.files[0] }))
                }
                className="border px-2 py-1 rounded mt-1"
              />
            </div>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Update
            </button>
          </form>
        ) : (
          <div>Loading...</div>
        )}
      </Modal>

      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Application"
        message="Are you sure you want to delete this application? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ApplicationResponses;
